/* eslint-disable no-restricted-imports */
import { Time } from "@/types/time";
import {
  addDays as addDaysFromDateFns,
  addHours as addHoursFromDateFns,
  addMilliseconds as addMillisecondsFromDateFns,
  addMinutes as addMinutesFromDateFns,
  addMonths as addMonthsFromDateFns,
  addSeconds as addSecondsFromDateFns,
  addWeeks as addWeeksFromDateFns,
  addYears as addYearsFromDateFns,
  differenceInCalendarDays as differenceInCalendarDaysFromDateFns,
  differenceInCalendarMonths as differenceInCalendarMonthsFromDateFns,
  differenceInCalendarYears as differenceInCalendarYearsFromDateFns,
  differenceInDays as differenceInDaysFromDateFns,
  differenceInHours as differenceInHoursFromDateFns,
  differenceInMinutes as differenceInMinutesFromDateFns,
  differenceInSeconds as differenceInSecondsFromDateFns,
  isBefore as isBeforeFromDateFns,
  isSameDay as isSameDayFromDateFns,
  isValid as isValidFromDateFns,
  parse as parseFromDateFns,
  parseISO as parseISOFromDateFns,
  setHours as setHoursFromDateFns,
  setMilliseconds as setMillisecondsFromDateFns,
  setMinutes as setMinutesFromDateFns,
  setSeconds as setSecondsFromDateFns,
  subDays as subDaysFromDateFns,
  subHours as subHoursFromDateFns,
  subMilliseconds as subMillisecondsFromDateFns,
  subMinutes as subMinutesFromDateFns,
  subMonths as subMonthsFromDateFns,
  subSeconds as subSecondsFromDateFns,
  subWeeks as subWeeksFromDateFns,
  subYears as subYearsFromDateFns
} from "date-fns";
/* eslint-enable no-restricted-imports */
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { ja } from "date-fns/locale";

export const convertToJST = (value: number) =>
  utcToZonedTime(
    zonedTimeToUtc(value, Intl.DateTimeFormat().resolvedOptions().timeZone),
    "Asia/Tokyo",
    {
      locale: ja
    }
  ).getTime();

export const convertToLocal = (value: number) =>
  utcToZonedTime(
    zonedTimeToUtc(value, "Asia/Tokyo"),
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    {
      locale: ja
    }
  ).getTime();

/**
 * 日付文字列 (ISO形式) から指定された日時フォーマット (locale: ja) に変換する
 * @param value
 * @param formatString
 * @returns 変換後の日時文字列
 */
export const formatFromISOString = (
  value: string,
  formatString = "yyyy/MM/dd HH:mm"
) => {
  // 日付のみ指定されていた場合、時分秒は 00:00:00 としておく
  if (/^\d{1,4}-\d{1,2}-\d{1,2}$/.test(value)) {
    return formatInTimeZone(
      parseISOFromDateFns(`${value}T00:00:00.000000+0900`),
      "Asia/Tokyo",
      formatString,
      {
        locale: ja
      }
    );
  }
  return formatInTimeZone(
    parseISOFromDateFns(value),
    "Asia/Tokyo",
    formatString,
    {
      locale: ja
    }
  );
};

/**
 * Unix タイムスタンプから指定された日時フォーマット (locale: ja) に変換する
 * @param value
 * @param formatString
 * @returns 変換後の日時文字列
 */
export const formatFromUnixTimestamp = (
  value: number,
  formatString = "yyyy-MM-dd"
) =>
  formatInTimeZone(value, "Asia/Tokyo", formatString, {
    locale: ja
  });

/**
 * 開始日付と終了日付から勤務時間のテキストを取得する
 */
export const getWorkingTimeText = (
  startWorkAt: string,
  finishWorkAt: string
): string => {
  // 開始日付と終了日付が同じ場合
  if (isSameDay(parseISO(startWorkAt), parseISO(finishWorkAt))) {
    return `${formatFromISOString(
      startWorkAt,
      "yy/MM/dd HH:mm"
    )} - ${formatFromISOString(finishWorkAt, "HH:mm")}`;
  }
  // 開始日付と終了日付が異なる場合
  return `${formatFromISOString(
    startWorkAt,
    "yy/MM/dd HH:mm"
  )} - ${formatFromISOString(finishWorkAt, "yy/MM/dd HH:mm")}`;
};

/**
 * 日付文字列を指定した文字で分割する
 * 有効な日付かどうかはチェックしない
 *
 * @param date 日付文字列
 * @returns 年、月、日の配列（分割できなかった場合はいずれも null）
 */
export const splitDate = (value: string | null, splitter = "-") => {
  if (
    !value ||
    !value.match(new RegExp(`\\d{4}${splitter}\\d{1,2}${splitter}\\d{1,2}`))
  ) {
    return [null, null, null];
  }
  return value.split(splitter).map(v => Number.parseInt(v, 10));
};

/**
 * 現在日時を取得する
 * @returns Unix タイムスタンプ
 */
// eslint-disable-next-line no-restricted-syntax
export const now = () => new Date().getTime();

/**
 * 日時をTime型に変換する
 */
export const parseTime = (value: string): Time => {
  const tokens = value.split(":");
  if (tokens.length < 2) {
    throw new Error("Invalid time format");
  }
  return {
    hour: parseInt(tokens[0]),
    minute: parseInt(tokens[1])
  };
};

/**
 * 日付と時刻を結合する
 */
export const combineDateAndTime = (date: number, time: string): number => {
  const timeObj = parseTime(time);
  return setHoursFromDateFns(
    setMinutesFromDateFns(
      setSecondsFromDateFns(setMillisecondsFromDateFns(date, 0), 0),
      timeObj.minute
    ),
    timeObj.hour
  ).getTime();
};

// 以下は date-fns の元関数をラップし、Date オブジェクトを受け取らないようにしている
// addXXX/subXXX に Unix タイムスタンプを渡すと内部で Date に変換されてしまうため、
// タイムゾーンによって月や日が変わってしまうケースにおいて計算が正しく実施されない
// そのため、一度日本時間分ずらしてから計算し、再度ローカルタイムゾーンの Unix タイムスタンプに変換してから返すようにしている
export const addYears = (value: number, amount: number) =>
  convertToLocal(addYearsFromDateFns(convertToJST(value), amount).getTime());
export const addMonths = (value: number, amount: number) =>
  convertToLocal(addMonthsFromDateFns(convertToJST(value), amount).getTime());
export const addWeeks = (value: number, amount: number) =>
  convertToLocal(addWeeksFromDateFns(convertToJST(value), amount).getTime());
export const addDays = (value: number, amount: number) =>
  convertToLocal(addDaysFromDateFns(convertToJST(value), amount).getTime());
export const addHours = (value: number, amount: number) =>
  addHoursFromDateFns(value, amount).getTime();
export const addMinutes = (value: number, amount: number) =>
  addMinutesFromDateFns(value, amount).getTime();
export const addSeconds = (value: number, amount: number) =>
  addSecondsFromDateFns(value, amount).getTime();
export const addMilliseconds = (value: number, amount: number) =>
  addMillisecondsFromDateFns(value, amount).getTime();
export const subYears = (value: number, amount: number) =>
  convertToLocal(subYearsFromDateFns(convertToJST(value), amount).getTime());
export const subMonths = (value: number, amount: number) =>
  convertToLocal(subMonthsFromDateFns(convertToJST(value), amount).getTime());
export const subWeeks = (value: number, amount: number) =>
  convertToLocal(subWeeksFromDateFns(convertToJST(value), amount).getTime());
export const subDays = (value: number, amount: number) =>
  convertToLocal(subDaysFromDateFns(convertToJST(value), amount).getTime());
export const subHours = (value: number, amount: number) =>
  subHoursFromDateFns(value, amount).getTime();
export const subMinutes = (value: number, amount: number) =>
  subMinutesFromDateFns(value, amount).getTime();
export const subMilliseconds = (value: number, amount: number) =>
  subMillisecondsFromDateFns(value, amount).getTime();
export const subSeconds = (value: number, amount: number) =>
  subSecondsFromDateFns(value, amount).getTime();
export const startOfMonth = (value: number) =>
  parseISO(formatFromUnixTimestamp(value, "yyyy-MM-01'T'00:00:00.000000+0900"));
// ローカルタイムゾーン基準だとずれるので、日本時間で当日00:00:00のフォーマットを使って変換する
export const startOfDay = (value: number) =>
  parseISO(formatFromUnixTimestamp(value, "yyyy-MM-dd'T'00:00:00.000000+0900"));
export const startOfToday = () => startOfDay(now());
export const startOfTomorrow = () => addDays(startOfToday(), 1);
export const startOfYesterday = () => subDays(startOfToday(), 1);
// ローカルタイムゾーン基準だとずれるので、日本時間で当日23:59:59のフォーマットを使って変換する
export const endOfMonth = (value: number) =>
  subDays(addMonths(startOfMonth(value), 1), 1);
export const endOfDay = (value: number) =>
  parseISO(formatFromUnixTimestamp(value, "yyyy-MM-dd'T'23:59:59.999999+0900"));
// 現地のタイムゾーンの情報が付与されてしまうのを防ぐため、+0900固定で返す
export const formatISO = (value: number) =>
  formatFromUnixTimestamp(value, "yyyy-MM-dd'T'HH:mm:ss.SSSSSS+0900");
export const parseISO = (value: string) => {
  // 日付のみ指定されていた場合、時分秒は 00:00:00 としておく
  if (/^\d{1,4}-\d{1,2}-\d{1,2}$/.test(value)) {
    return parseISOFromDateFns(`${value}T00:00:00.000000+0900`).getTime();
  }
  return parseISOFromDateFns(value).getTime();
};
export const differenceInCalendarYears = (
  dateLeft: number,
  dateRight: number
) =>
  differenceInCalendarYearsFromDateFns(
    convertToJST(dateLeft),
    convertToJST(dateRight)
  );
export const differenceInCalendarMonths = (
  dateLeft: number,
  dateRight: number
) =>
  differenceInCalendarMonthsFromDateFns(
    convertToJST(dateLeft),
    convertToJST(dateRight)
  );
export const differenceInCalendarDays = (dateLeft: number, dateRight: number) =>
  differenceInCalendarDaysFromDateFns(
    convertToJST(dateLeft),
    convertToJST(dateRight)
  );
export const differenceInDays = (dateLeft: number, dateRight: number) =>
  differenceInDaysFromDateFns(dateLeft, dateRight);
export const differenceInHours = (dateLeft: number, dateRight: number) =>
  differenceInHoursFromDateFns(dateLeft, dateRight);
export const differenceInMinutes = (dateLeft: number, dateRight: number) =>
  differenceInMinutesFromDateFns(dateLeft, dateRight);
export const differenceInSeconds = (dateLeft: number, dateRight: number) =>
  differenceInSecondsFromDateFns(dateLeft, dateRight);
export const eachDayOfInterval = (start: number, end: number) => {
  const days: number[] = [];
  let accumulator = start;
  while (end >= accumulator) {
    days.push(accumulator);
    accumulator = addDays(accumulator, 1);
  }
  return days;
};
export const isSameDay = (value1: number, value2: number) =>
  isSameDayFromDateFns(startOfDay(value1), startOfDay(value2));
export const isToday = (value: number) => isSameDay(value, startOfToday());
export const isTomorrow = (value: number) =>
  isSameDay(value, startOfTomorrow());
export const isValid = (value: number) => isValidFromDateFns(value);
export const getYear = (value: number) =>
  Number(formatFromUnixTimestamp(value, "y"));
export const getMonth = (value: number) =>
  Number(formatFromUnixTimestamp(value, "M"));
// 月のインデックスを返すため -1 する
export const getMonthIndex = (value: number) => getMonth(value) - 1;
export const getDate = (value: number) =>
  Number(formatFromUnixTimestamp(value, "d"));
// 1~7 (7が日曜)
export const getDay = (value: number) =>
  Number(formatFromUnixTimestamp(value, "i"));
export const getDayLabel = (value: number) =>
  formatFromUnixTimestamp(value, "E");
export const getHour = (value: number) =>
  Number(formatFromUnixTimestamp(value, "H"));
export const parse = (value: string, formatString: string) =>
  parseFromDateFns(value, formatString, now()).getTime();
export const isAfter = (dateLeft: number, dateRight: number) =>
  dateLeft > dateRight;
export const isBefore = (date: Date | number, dateToCompare: Date | number) =>
  isBeforeFromDateFns(date, dateToCompare);
export const calcDate = (year: number, month: number, day: number) => {
  const paddedMonth = month.toString().padStart(2, "0");
  const paddedDay = day.toString().padStart(2, "0");
  return parseISO(`${year}-${paddedMonth}-${paddedDay}T00:00:00.000000+0900`);
};
